import React, { useRef, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress
} from '@mui/material';
import { createJobDefinition } from 'graphql/mutations';
import { JobForm } from './JobForm';
import { useUser } from 'components/RDAuthContext'

// Constants for Phase 0 (to be replaced with Data Source Lambda in future phases)
const PHASE_0_CONSTANTS = {
  DATA_SOURCES: [
    { id: 'toast-pos', name: 'Toast POS' }
  ],
  SOURCE_ENDPOINTS: [
    { id: 'orders', name: 'Orders', dataSourceId: 'toast-pos' }
  ],
  // Define more constants as needed
};

export const CreateJobModal = ({ initialData, onClose }) => {
  
  const formRef = useRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user } = useUser()
  console.log('user', user)
  
  // Default values for a new job aligned with JobDefinition schema
  const defaultValues = {
    name: '',
    description: '',
    dataSource: 'toast-pos', // Fixed to Toast for Phase 0
    sourceEndpoint: 'orders', // Default endpoint
    sourceFilters: [
      { name: 'storeIds', values: ['*'] }
    ],
    dataTimeConfig: {
      mode: 'ADAPTER_DEFAULT',
      relativeValue: null,
      relativeUnit: null,
      startDateTime: null,
      endDateTime: null,
      granularity: null
    },
    scheduleConfig: {
      mode: 'RECURRING', // Default to RECURRING for Phase 0
      cronExpression: '0 0 * * ? *', // Default daily at midnight
      timeZoneStrategy: 'SYSTEM_DEFAULT',
      specificTimeZone: null,
      scheduleStartDate: null,
      scheduleEndDate: null,
      scheduledTime: null
    },
    status: 'ACTIVE',
    settings: {},
    ...(initialData || {}), // Override with initialData if provided (for cloning)
  };

  const handleSubmit = async (formData) => {
    console.log('handleSubmit formData', formData);

    // Check for clientId before proceeding
    if (!formData.clientId && !user?.client) {
      console.error('Error: No clientId available for job definition');
      setError('Unable to create job: Client ID is required but not available. Please contact support.');
      return; // Exit early - don't even set loading state
    }

    setLoading(true);
    setError(null);

    try {
      // Generate a unique ID for new job definition
      const jobDefinitionId = uuidv4();
      
      // Get current user information
      const username = user.userName
      const clientId = formData.clientId || user.client

      if (!clientId) {
        throw new Error('Client ID is required but not available')
      }
      
      // Get current timestamp
      const now = new Date().toISOString();

      // Fix date formats for ABSOLUTE mode
      let dataTimeConfig = {
        mode: formData.dataTimeConfig.mode
      }

      if (formData.dataTimeConfig.mode === 'RELATIVE') {
        dataTimeConfig.relativeValue = formData.dataTimeConfig.relativeValue;
        dataTimeConfig.relativeUnit = formData.dataTimeConfig.relativeUnit;
        if (formData.dataTimeConfig.granularity) {
          dataTimeConfig.granularity = formData.dataTimeConfig.granularity;
        }
      } else if (formData.dataTimeConfig.mode === 'ABSOLUTE') {
        // Ensure dates are in proper AWS DateTime format
        if (formData.dataTimeConfig.startDateTime) {
          try {
            // Parse the date and convert to full ISO string
            const startDate = new Date(formData.dataTimeConfig.startDateTime);
            if (isNaN(startDate.getTime())) {
              throw new Error('Invalid start date');
            }
            dataTimeConfig.startDateTime = startDate.toISOString();
          } catch (err) {
            console.error('Error formatting startDateTime:', err);
            setError('Invalid format for Start Date');
            setLoading(false);
            return;
          }
        }
        
        if (formData.dataTimeConfig.endDateTime) {
          try {
            // Parse the date and convert to full ISO string
            const endDate = new Date(formData.dataTimeConfig.endDateTime);
            if (isNaN(endDate.getTime())) {
              throw new Error('Invalid end date');
            }
            dataTimeConfig.endDateTime = endDate.toISOString();
          } catch (err) {
            console.error('Error formatting endDateTime:', err);
            setError('Invalid format for End Date');
            setLoading(false);
            return;
          }
        }
        
        if (formData.dataTimeConfig.granularity) {
          dataTimeConfig.granularity = formData.dataTimeConfig.granularity;
        }
      }
      
      // Handle schedule config
      let scheduleConfig = {
        mode: formData.scheduleConfig.mode,
        timeZoneStrategy: formData.scheduleConfig.timeZoneStrategy,
      };
      
      if (formData.scheduleConfig.mode === 'RECURRING') {
        scheduleConfig.cronExpression = formData.scheduleConfig.cronExpression;
      } else if (formData.scheduleConfig.mode === 'ONE_TIME') {
        // Ensure scheduledTime is in proper format
        if (formData.scheduleConfig.scheduledTime) {
          try {
            const scheduledDate = new Date(formData.scheduleConfig.scheduledTime);
            if (isNaN(scheduledDate.getTime())) {
              throw new Error('Invalid scheduled time');
            }
            scheduleConfig.scheduledTime = scheduledDate.toISOString();
          } catch (err) {
            console.error('Error formatting scheduledTime:', err);
            setError('Invalid format for Scheduled Time');
            setLoading(false);
            return;
          }
        }
      }
      
      // Handle time zone settings
      if (formData.scheduleConfig.timeZoneStrategy === 'SPECIFIC') {
        scheduleConfig.specificTimeZone = formData.scheduleConfig.specificTimeZone;
      }
      
      // Handle optional schedule window dates
      if (formData.scheduleConfig.scheduleStartDate) {
        try {
          const startDate = new Date(formData.scheduleConfig.scheduleStartDate);
          if (!isNaN(startDate.getTime())) {
            scheduleConfig.scheduleStartDate = startDate.toISOString();
          }
        } catch (err) {
          console.error('Error formatting scheduleStartDate:', err);
        }
      }
      
      if (formData.scheduleConfig.scheduleEndDate) {
        try {
          const endDate = new Date(formData.scheduleConfig.scheduleEndDate);
          if (!isNaN(endDate.getTime())) {
            scheduleConfig.scheduleEndDate = endDate.toISOString();
          }
        } catch (err) {
          console.error('Error formatting scheduleEndDate:', err);
        }
      }
      
      // Prepare the input for GraphQL mutation - aligned with JobDefinition schema
      const input = {
        jobDefinitionId,
        name: formData.name,
        description: formData.description || '',
        clientId: clientId, // You may want to get this from context
        
        // Source configuration
        dataSource: formData.dataSource,
        sourceEndpoint: formData.sourceEndpoint,
        sourceFilters: formData.sourceFilters,
        
        // Data Time Configuration
        dataTimeConfig,
        
        // Schedule Configuration
        scheduleConfig,
        
        // Status and metadata
        status: formData.status,
        createdBy: username,
        createdAt: now,
        lastModified: now,
        
        // Optional configuration and composite key
        settings: JSON.stringify(formData.settings || {}),
        clientDataSource: `${clientId}#${formData.dataSource}`,
      };

      console.log('Submitting to GraphQL:', input)

      // Execute the mutation
      const result = await API.graphql(graphqlOperation(createJobDefinition, { input }));

      console.log('GraphQL mutation result:', result)
      
      // Close modal and refresh the list
      onClose(true);
    } catch (err) {
      console.error('Error creating job definition:', err);
      setError(err.message || 'Failed to create job definition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={() => !loading && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {initialData ? 'Clone Job Definition' : 'Create New Job Definition'}
      </DialogTitle>
      
      <DialogContent>
        <JobForm 
          ref={formRef}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          error={error}
          loading={loading}
          dataSources={PHASE_0_CONSTANTS.DATA_SOURCES}
          sourceEndpoints={PHASE_0_CONSTANTS.SOURCE_ENDPOINTS}
        />
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose()} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          // type="submit"
          // form="job-definition-form"           
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};